import { useId } from 'react'
import { cn } from '@/lib/utils'

interface GradientSparkleIconProps {
  className?: string
  /**
   * Diagonal gradient stops. Defaults are saturated iris → violet (the AI
   * 'shimmer' sweep: --ai-accent 240° → --ai-grad-to 280°). They're kept
   * saturated and mid-toned — not pale — so the thin stroke stays legible at
   * small sizes (10–14px) on light surfaces, while still reading on the black
   * primary button. Pass explicit colors to override per surface.
   */
  fromColor?: string
  toColor?: string
  /**
   * Render a flat, single-color star that follows `currentColor` instead of the
   * iris→violet gradient. Use when the icon sits inside a saturated colored chip
   * (e.g. a purple AI tile) where the gradient stroke would vanish — pair with a
   * text color class like `text-white` to set the stroke.
   */
  solid?: boolean
}

/**
 * Single "star" sparkle (the 4-pointed kind) rendered with a saturated
 * azure → violet gradient stroke. Preferred over Lucide's `Sparkles` when we
 * want one focal sparkle rather than the multi-sparkle cluster.
 *
 * The gradient is defined inline with a unique id (via useId) so multiple
 * instances on the page don't share defs and stays SSR-safe.
 */
export function GradientSparkleIcon({
  className,
  // Saturated azure → violet (matches the AI shimmer). Mid-toned, not pale, so
  // the thin stroke survives at 10–14px on light surfaces yet still reads on
  // black — both stops clear 4:1 on white AND on black, which the iris pair did
  // not. The old mint-sage (hue 88) stop is gone — it predated the brand accent.
  fromColor = 'hsl(215 92% 55%)',
  toColor = 'hsl(259 88% 66%)',
  solid = false,
}: GradientSparkleIconProps) {
  const gradientId = useId()
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={solid ? 'currentColor' : `url(#${gradientId})`}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('h-4 w-4', className)}
      aria-hidden="true"
    >
      {!solid && (
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={fromColor} />
            <stop offset="100%" stopColor={toColor} />
          </linearGradient>
        </defs>
      )}
      {/* Symmetric 4-pointed star centered on (12, 12). Each arm is the
          same length so the icon reads as balanced regardless of size. */}
      <path d="M12 2 L13.8 10.2 L22 12 L13.8 13.8 L12 22 L10.2 13.8 L2 12 L10.2 10.2 Z" />
    </svg>
  )
}
